import { BookOpen, Crosshair, Home, Pencil, Plus, Route, Settings2, X } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import { eventCountdown, personCountdown, relativeText } from '../../model/countdown';
import { dateSortKey, formatDate } from '../../model/dates';
import { childrenOf, graphOf, other, siblingsOf } from '../../model/graph';
import { headlineRelations, relate } from '../../model/kinship';
import { displayName, eventTitle, genderLabel, isHiddenName, LIFE_STATUS_LABELS, lifeSpanLong, lifeStatus, nameToString, presumedDeceased, primaryName, shortName, visibleNames, pronounSet } from '../../model/people';
import type { Id, LifeEvent, Relationship } from '../../model/types';
import { GROUP_KIND_LABELS, NAME_TYPE_LABELS, termsDescription, PARENT_TYPE_LABELS, PARTNER_STATUS_LABELS, PARTNER_TYPE_LABELS, SIBLING_TYPE_LABELS, SOCIAL_TYPE_LABELS } from '../labels';
import { closeDialog, commit, focusPerson, getState, openDialog, replaceDialog, selectPerson, setState, setView, toast, useApp } from '../store';
import { citationsAbout, mediaOf, sourceLabel, todosOf } from '../../model/research';
import { MediaFileInput, MediaThumb } from '../views/MediaView';
import { TodoRow } from '../views/TodosView';
import { targetLabel } from './CitationEditor';
import { QUALITY_LABELS } from '../labels';
import { Avatar, JoinedTerms, PersonRow } from './common';

/**
 * A person's details. On the charts it's the sidebar; elsewhere (People, Relate, Groups, Reports)
 * it opens in a dialog, where clicking a relative shows them in the same dialog.
 */
export function PersonPanel({ personId, inDialog }: { personId: Id; inDialog?: boolean }) {
  const tree = useApp((s) => s.tree)!;
  const mediaInput = useRef<HTMLInputElement>(null);
  const p = tree.people[personId];
  if (!p) return null;
  const g = graphOf(tree);
  const home = tree.homePersonId;
  const homeRels = home && home !== personId && tree.people[home] ? headlineRelations(relate(tree, home, personId)) : [];

  const go = (id: Id) => (inDialog ? replaceDialog({ type: 'person-details', personId: id }) : focusPerson(id));
  const leave = () => inDialog && closeDialog();
  const editRel = (r: Relationship) => (
    <button className="btn icon small ghost" aria-label="Edit relationship" title="Edit relationship" onClick={() => openDialog({ type: 'edit-relationship', relId: r.id })}>
      <Settings2 size={14} />
    </button>
  );
  const add = (kind: 'parent' | 'child' | 'partner' | 'sibling' | 'social', label: string) => (
    <button className="add-link" onClick={() => openDialog({ type: 'add-relative', personId, kind })} aria-label={`Add ${kind === 'social' ? 'friend or other connection' : kind}`}>
      <Plus size={14} /> {label}
    </button>
  );

  const parents = g.parentRels.get(personId) ?? [];
  const partners = g.partnerRels.get(personId) ?? [];
  const kids = g.childRels.get(personId) ?? [];
  const sibs = siblingsOf(g, personId);
  const social = g.socialRels.get(personId) ?? [];
  const groups = g.groupsOf.get(personId) ?? [];
  const name = primaryName(p);
  const otherNames = visibleNames(p).slice(1);
  const hiddenCount = p.names.filter(isHiddenName).length;
  const events = [...p.events].sort((a, b) => dateSortKey(a.date) - dateSortKey(b.date));
  const countdown = personCountdown(p);
  const status = lifeStatus(p);
  const media = mediaOf(tree, personId);
  const cites = citationsAbout(tree, personId);
  const todos = todosOf(tree, personId);
  const openTodos = todos.filter((t) => t.status !== 'done');

  const socialLabel = (r: Extract<Relationship, { kind: 'social' }>) => {
    if (r.label) return r.label;
    if (r.type === 'mentor') return r.a === personId ? 'Mentee' : 'Mentor';
    return SOCIAL_TYPE_LABELS[r.type];
  };

  const Root = inDialog ? 'div' : 'aside';
  return (
    <Root className={`panel${inDialog ? ' in-dialog' : ''}`} aria-label={`Details for ${displayName(p)}`}>
      <div className="panel-head">
        <span className="avatar-ring">
          <Avatar person={p} size="lg" />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2>{displayName(p)}</h2>
          {name?.nickname && (
            <div className="panel-nickname">
              Goes by <b>{name.nickname}</b>
            </div>
          )}
          <div className="sub">{lifeSpanLong(p) || (status === 'unknown' ? 'Living status not recorded' : LIFE_STATUS_LABELS[status])}</div>
          {countdown && <div className="countdown">{countdown}</div>}
          <div className="row wrap" style={{ marginTop: 6, gap: 4 }}>
            {p.gender.identity !== 'unknown' && <span className="badge">{genderLabel(p.gender)}</span>}
            {p.pronouns && <span className="badge accent">{p.pronouns}</span>}
            {home === personId && <span className="badge accent"><Home size={11} /> Home person</span>}
            {status === 'deceased' && <span className="badge">Deceased</span>}
            {status === 'unknown' && lifeSpanLong(p) && <span className="badge faint-badge" title="Not recorded as living or deceased. Set it in Edit.">Living status not recorded</span>}
          </div>
        </div>
        <button className="btn icon ghost" onClick={() => (inDialog ? closeDialog() : selectPerson(null))} aria-label="Close details">
          <X size={18} />
        </button>
      </div>
      <div className="panel-actions">
        <button className="btn small primary" onClick={() => openDialog({ type: 'edit-person', personId })}>
          <Pencil size={14} /> Edit
        </button>
        <button
          className="btn small"
          onClick={() => {
            leave();
            focusPerson(personId, { view: getState().view === 'network' ? 'network' : 'tree' });
          }}
          title="Center the chart on this person"
        >
          <Crosshair size={14} /> {inDialog ? 'Show in tree' : 'Center'}
        </button>
        <button
          className="btn small"
          onClick={() => {
            leave();
            setState(home && home !== personId ? { relateA: home, relateB: personId } : { relateA: personId, relateB: null });
            setView('relate');
          }}
        >
          <Route size={14} /> How related?
        </button>
        {home !== personId && (
          <button
            className="btn small ghost"
            onClick={() => {
              commit({ ...getState().tree!, homePersonId: personId });
              toast(`${shortName(p)} is now the home person. Relationships are described relative to ${pronounSet(p).obj}.`, 'success');
            }}
            title="Relationships are shown relative to the home person"
          >
            <Home size={14} /> Make home
          </button>
        )}
      </div>

      {homeRels.length > 0 && (
        <div className="panel-section">
          <div className="rel-callout" title={homeRels.map((r) => r.explanation).join(' ')}>
            {shortName(p)} is {displayName(tree.people[home!])}'s <JoinedTerms terms={homeRels.map((r) => r.term)} />.
          </div>
        </div>
      )}

      <Section title="Identity">
        <dl className="panel-facts">
          <dt>Name</dt>
          <dd>
            {nameToString(name, { nickname: true }) || <NotRecorded />}
            {name && name.type !== 'name' && <span className="faint"> · {NAME_TYPE_LABELS[name.type]}</span>}
          </dd>
          <dt>Gender</dt>
          <dd>{p.gender.identity !== 'unknown' ? genderLabel(p.gender) : <NotRecorded />}</dd>
          <dt>Pronouns</dt>
          <dd>{p.pronouns || <NotRecorded />}</dd>
          <dt>Relationship words</dt>
          <dd>{termsDescription(p)}</dd>
          <dt>Living status</dt>
          <dd>{p.living === true ? 'Living' : p.living === false ? 'Deceased' : presumedDeceased(p) ? 'Deceased, from their records' : <NotRecorded />}</dd>
        </dl>
      </Section>

      {(otherNames.length > 0 || hiddenCount > 0) && (
        <Section title="Other names">
          {otherNames.map((n, i) => (
            <div key={i} className="small-text">
              {nameToString(n, { nickname: true })} {n.type !== 'name' && <span className="faint">· {NAME_TYPE_LABELS[n.type]}</span>}
            </div>
          ))}
          {hiddenCount > 0 && <div className="faint small-text">{hiddenCount} hidden name{hiddenCount > 1 ? 's' : ''}</div>}
        </Section>
      )}

      <Section title="Parents" action={add('parent', 'Add')}>
        {parents.map((r) => (
          <PersonRow key={r.id} person={tree.people[r.a]} meta={r.type !== 'biological' ? PARENT_TYPE_LABELS[r.type] : undefined} onClick={() => go(r.a)} actions={editRel(r)} />
        ))}
        {!parents.length && <Empty>No parents recorded.</Empty>}
      </Section>

      <Section title="Partners" action={add('partner', 'Add')}>
        {partners.map((r) => (
          <PersonRow
            key={r.id}
            person={tree.people[other(r, personId)]}
            meta={[r.label || PARTNER_TYPE_LABELS[r.type], r.status !== 'current' ? PARTNER_STATUS_LABELS[r.status].replace(/ \(.*\)/, '') : '', formatDate(r.start)].filter(Boolean).join(', ')}
            onClick={() => go(other(r, personId))}
            actions={editRel(r)}
          />
        ))}
        {!partners.length && <Empty>No partners recorded.</Empty>}
      </Section>

      <Section title="Children" action={add('child', 'Add')}>
        {kids.map((r) => {
          const otherParents = (g.parentRels.get(r.b) ?? []).filter((x) => x.a !== personId).map((x) => shortName(tree.people[x.a]));
          return (
            <PersonRow
              key={r.id}
              person={tree.people[r.b]}
              meta={[r.type !== 'biological' ? PARENT_TYPE_LABELS[r.type] : '', otherParents.length ? `with ${otherParents.join(' & ')}` : ''].filter(Boolean).join(' · ')}
              onClick={() => go(r.b)}
              actions={editRel(r)}
            />
          );
        })}
        {!kids.length && <Empty>No children recorded.</Empty>}
      </Section>

      <Section title="Siblings" action={add('sibling', 'Add')}>
        {sibs.map((s) => (
          <PersonRow
            key={s.id}
            person={tree.people[s.id]}
            meta={s.type !== 'unknown' && s.type !== 'full' ? SIBLING_TYPE_LABELS[s.type] : undefined}
            onClick={() => go(s.id)}
            actions={s.explicit ? editRel(s.explicit) : undefined}
          />
        ))}
        {!sibs.length && <Empty>No siblings recorded.</Empty>}
      </Section>

      <Section title="Friends & connections" action={add('social', 'Add')}>
        {social.map((r) => (
          <PersonRow key={r.id} person={tree.people[other(r, personId)]} meta={socialLabel(r)} onClick={() => go(other(r, personId))} actions={editRel(r)} />
        ))}
        {!social.length && <Empty>No friends or other connections recorded.</Empty>}
      </Section>

      {groups.length > 0 && (
        <Section title="Groups">
          <div className="row wrap">
            {groups.map((grp) => (
              <button key={grp.id} className="chip group-tint" style={{ '--gc': grp.color, border: 0, cursor: 'pointer' } as React.CSSProperties} onClick={() => openDialog({ type: 'group-details', groupId: grp.id })} title={`${GROUP_KIND_LABELS[grp.kind]}: show the group`}>
                {grp.name}
                {grp.members.find((m) => m.personId === personId)?.role ? ` · ${grp.members.find((m) => m.personId === personId)!.role}` : ''}
              </button>
            ))}
          </div>
        </Section>
      )}

      <Section title="Life events">
        {events.length ? (
          <div className="timeline">
            {events.map((e) => (
              <div key={e.id} className="tl-item">
                <div className="when">{formatDate(e.date) || '—'}</div>
                <div>
                  <div className="what">{eventTitle(e)}</div>
                  {e.place && <div className="where">{e.place}</div>}
                  <EventTiming e={e} />
                  {e.note && <div className="small-text muted">{e.note}</div>}
                  {e.citations?.length ? (
                    <div className="event-sources">
                      <BookOpen size={11} /> {e.citations.map((c) => sourceLabel(tree.sources[c.sourceId])).join('; ')}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty>No life events yet.</Empty>
        )}
      </Section>

      <Section
        title="Media"
        action={
          <button className="add-link" onClick={() => mediaInput.current?.click()} aria-label="Add photos or documents">
            <Plus size={14} /> Add
          </button>
        }
      >
        <MediaFileInput inputRef={mediaInput} personIds={[personId]} />
        {media.length ? (
          <div className="panel-media">
            {media.slice(0, 12).map((m) => (
              <button key={m.id} className="panel-media-item" title={m.title} onClick={() => openDialog({ type: 'media-details', mediaId: m.id })}>
                <MediaThumb item={m} />
              </button>
            ))}
            {media.length > 12 && (
              <button className="panel-media-item more" onClick={() => {
                leave();
                setView('media');
              }}>
                +{media.length - 12}
              </button>
            )}
          </div>
        ) : (
          <Empty>No photos, documents, or recordings.</Empty>
        )}
      </Section>

      <Section title="Sources" action={<button className="add-link" onClick={() => openDialog({ type: 'edit-citation', personId })} aria-label="Cite a source"><Plus size={14} /> Cite</button>}>
        {cites.length ? (
          <div className="stack tight">
            {cites.map((c, i) => (
              <button key={i} className="list-row compact" onClick={() => openDialog({ type: 'edit-citation', personId, target: c.target, index: c.index })}>
                <BookOpen size={15} className="faint" />
                <span className="grow">
                  <span className="title">{sourceLabel(tree.sources[c.citation.sourceId])}</span>
                  <span className="meta">
                    {[targetLabel(tree, c.target, personId), c.citation.page, c.citation.quality !== undefined ? QUALITY_LABELS[c.citation.quality] : ''].filter(Boolean).join(' · ')}
                  </span>
                </span>
              </button>
            ))}
          </div>
        ) : (
          <Empty>No sources cited.</Empty>
        )}
      </Section>

      <Section title="To-dos" action={<button className="add-link" onClick={() => openDialog({ type: 'edit-todo', todoId: null, personIds: [personId] })} aria-label="Add a to-do"><Plus size={14} /> Add</button>}>
        {openTodos.map((t) => (
          <TodoRow key={t.id} todo={t} hidePerson={personId} />
        ))}
        {!openTodos.length && <Empty>{todos.length ? 'All done.' : 'Nothing to do.'}</Empty>}
        {todos.length > openTodos.length && openTodos.length > 0 && <Empty>{todos.length - openTodos.length} done</Empty>}
      </Section>

      {p.tags.length > 0 && (
        <Section title="Tags">
          <div className="row wrap">
            {p.tags.map((t) => (
              <span key={t} className="badge">
                {t}
              </span>
            ))}
          </div>
        </Section>
      )}

      {p.notes && (
        <Section title="Notes">
          <div className="notes-text">{p.notes}</div>
        </Section>
      )}
      {childrenOf(g, personId).length === 0 && parents.length === 0 && partners.length === 0 && social.length === 0 && (
        <div className="panel-section faint small-text">Tip: use the “Add” links above to connect {primaryName(p)?.given || 'this person'} to others.</div>
      )}
    </Root>
  );
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="panel-section">
      <h3>
        {title}
        <span className="spacer" />
        {action}
      </h3>
      {children}
    </section>
  );
}

/** "3 years ago · HRT birthday in 23 days (3rd)". Birthdays are in the header instead. */
function EventTiming({ e }: { e: LifeEvent }) {
  if (e.type === 'birth') return null;
  const since = relativeText(e.date);
  const next = eventCountdown(e);
  if (!since && !next) return null;
  return (
    <div className="event-timing">
      {since}
      {since && next ? ' · ' : ''}
      {next && <b>{next}</b>}
    </div>
  );
}

function NotRecorded() {
  return <span className="faint">Not recorded</span>;
}

function Empty({ children }: { children: ReactNode }) {
  return <div className="faint small-text">{children}</div>;
}
